import Alert                       from '@widesk-ui/components/Alert';
import React                       from 'react';
import View                        from '@widesk-ui/components/View';
import checkMapLinks               from '@/TokamapEditor/helpers/checkMapLinks';
import currentProjectEditorStore   from '@/TokamapEditor/stores/currentProjectEditorStore';
import useDesignTokens             from '@/hooks/useDesignTokens';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { hasNoLinkIssue }          from '@/TokamapEditor/helpers/checkMapLinks';
import { InfoCircleFilled }        from '@ant-design/icons';
import { observer }                from 'mobx-react-lite';

import type { MapLinksReport } from '@/TokamapEditor/helpers/checkMapLinks';

const ALERT_PADDING = { padding: '10px 12px' };

/** Un défaut, les cartes qu'il concerne, et ce qu'il fait au visiteur — voir `groupIssues`. */
interface LinkIssueGroup {
	subjects: string[];
	detail: string;
}

/**
 * Ce que les visiteurs ne sauront pas atteindre dans le projet qu'on s'apprête à publier.
 *
 * Signalé ici parce qu'une carte ne dit pas ce qui mène à elle : le défaut ne se voit nulle part en
 * édition, et publier est le dernier moment où l'annoncer serve encore. N'empêche jamais de publier
 * — une carte à l'écart est parfois voulue, et le contrôle ne le sait pas.
 */
const MapLinksNotice = observer(() => {
	const tokens = useDesignTokens();
	// `undefined` tant que le contrôle tourne, `null` quand il n'a rien à dire (voir `checkMapLinks`).
	const [report, setReport] = React.useState<MapLinksReport | null | undefined>(undefined);
	// Relance le contrôle si les cartes du projet arrivent après l'ouverture de la modale, qui
	// resterait sinon sur "moins de deux cartes, rien à contrôler".
	const mapCount = currentProjectEditorStore.get.mapCollection.models.length;

	React.useEffect(() => {
		let abandoned = false;

		checkMapLinks()
			.catch(err => {
				console.error('Publication : contrôle des liaisons entre cartes', err);

				return null;
			})
			.then(result => {
				if (!abandoned) setReport(result);
			});

		return () => {
			abandoned = true;
		};
	}, [mapCount]);

	if (report === undefined) {
		return (
			<View size="small" color={tokens.text.muted} lineHeight={1.5}>
				Vérification des liaisons entre vos cartes…
			</View>
		);
	}

	if (!report) return null;

	if (hasNoLinkIssue(report)) {
		return (
			<View size="small" color={tokens.text.muted} lineHeight={1.5}>
				Toutes vos cartes se rejoignent : le visiteur peut passer de n'importe laquelle à n'importe
				quelle autre.
			</View>
		);
	}

	if (isUnlinkedProject(report)) {
		return (
			<Alert
				type="info"
				style={ALERT_PADDING}
				message={(
					<View lineHeight={1.45} style={{ display: 'block' }}>
						<InfoCircleFilled style={{ color: tokens.color.primary, marginRight: 6 }} />
						Aucune zone de ce projet ne mène à une autre carte : le visiteur ne pourra pas passer de
						l'une à l'autre, sauf en cherchant un point d'intérêt.
					</View>
				)}
			/>
		);
	}

	return (
		<Alert
			type="warning"
			// Icône posée à la main, `showIcon` laissé de côté : antd lui réserve sinon une colonne à
			// gauche sous laquelle toutes les lignes s'alignent, et le texte y perd la largeur de la modale.
			style={ALERT_PADDING}
			message={(
				<View gap="xs" lineHeight={1.45}>
					<View row centerV gap={8}>
						<ExclamationCircleFilled
							style={{ color: tokens.color.warning, fontSize: 24, marginTop: 1 }}
						/>

						<View bold>À vérifier avant de publier</View>
					</View>

					{groupIssues(report).map(group => (
						// `display: block` : une seule phrase qui s'enroule, là où `View` empilerait ses enfants.
						<View key={group.detail} style={{ display: 'block' }}>
							<strong>{group.subjects.join(', ')}</strong> — {group.detail}
						</View>
					))}
				</View>
			)}
		/>
	);
});

export default MapLinksNotice;

/** Aucune zone du projet ne mène à une autre carte : ses cartes ne sont pas brouillées, juste séparées. */
function isUnlinkedProject(report: MapLinksReport): boolean {
	return report.groups.every(group => group.length === 1)
		&& !report.oneWayLinks.length
		&& !report.brokenLinks.length;
}

/** Un groupe par défaut constaté, dans l'ordre où il vaut la peine d'être corrigé. */
function groupIssues(report: MapLinksReport): LinkIssueGroup[] {
	const groups: LinkIssueGroup[] = [];

	if (report.oneWayLinks.length) {
		groups.push({
			subjects: report.oneWayLinks.map(({ from, to }) => `${from} → ${to}`),
			detail: 'aucune zone ne ramène : le visiteur y restera, et aucun itinéraire ne traversera.',
		});
	}

	// Une carte prise dans une liaison à sens unique n'est pas dite isolée en plus : c'est la même
	// anomalie, déjà signalée plus précisément.
	const oneWayMaps = new Set(report.oneWayLinks.flatMap(link => [link.from, link.to]));

	// Le premier groupe est le plus grand : le projet tel qu'on le parcourt, les suivants en sont coupés.
	const cutOff = report.groups.slice(1).filter(group => group.every(label => !oneWayMaps.has(label)));
	const isolated = cutOff.filter(group => group.length === 1).flat();

	if (isolated.length) {
		groups.push({
			subjects: isolated,
			detail: 'aucune zone n\'y mène, le visiteur ne pourra y arriver qu\'en cherchant un de '
				+ `${isolated.length > 1 ? 'leurs' : 'ses'} points d'intérêt.`,
		});
	}

	for (const group of cutOff.filter(group => group.length > 1)) {
		groups.push({
			subjects: group,
			detail: 'ces cartes ne se rejoignent qu\'entre elles, à l\'écart du reste du projet.',
		});
	}

	if (report.brokenLinks.length) {
		groups.push({
			subjects: report.brokenLinks,
			detail: `${report.brokenLinks.length > 1 ? 'une de leurs zones' : 'une de ses zones'} mène à `
				+ 'une carte supprimée : le clic ne fera rien.',
		});
	}

	return groups;
}
